import React, { useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { format } from "date-fns";

interface Props {
  invoiceData: any;
  /** Optional array of grouped invoices for a common receipt */
  groupedInvoices?: any[];
  studentData: any;
  onClose: () => void;
}

const ReceiptModal: React.FC<Props> = ({ invoiceData, groupedInvoices, studentData, onClose }) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  // Determine if this is a grouped receipt
  const isGrouped = groupedInvoices && groupedInvoices.length > 1;
  const invoices = isGrouped ? groupedInvoices : [invoiceData];

  // Calculate totals across all invoices in the receipt
  const totalAmount = invoices!.reduce((sum: number, inv: any) => sum + Number(inv.amount || 0), 0);
  const totalDiscount = invoices!.reduce((sum: number, inv: any) => sum + Number(inv.discount || 0), 0);
  const totalPaid = totalAmount - totalDiscount;

  // Build reference string
  const refString = isGrouped
    ? groupedInvoices!.map((inv: any) => inv.invoice_number || inv.reference).join(', ')
    : invoiceData?.invoice_number || invoiceData?.reference;

  const handleDownload = async () => {
    if (receiptRef.current) {
      try {
        const canvas = await html2canvas(receiptRef.current, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "px",
          format: "a4",
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`Receipt_${invoices![0]?.invoice_number || invoices![0]?.reference || 'payment'}.pdf`);
      } catch (err) {
        console.error("Error generating PDF", err);
      }
    }
  };

  if (!invoiceData && (!groupedInvoices || groupedInvoices.length === 0)) return null;

  // Use the first invoice for shared details
  const primaryInvoice = invoices![0];

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
      <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1051 }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="modal-title">Payment Receipt</h5>
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-primary btn-sm me-2"
                  onClick={handleDownload}
                >
                  <i className="ti ti-download me-1" />
                  Download PDF
                </button>
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={onClose}
                >
                  <i className="ti ti-x me-1" />
                  Close
                </button>
              </div>
            </div>
            <div className="modal-body p-4" style={{ backgroundColor: "#f8f9fa" }}>
              <div
                ref={receiptRef}
                className="bg-white p-4 border rounded shadow-sm"
                style={{ width: "100%", margin: "0 auto", position: "relative" }}
              >
                {/* Header with Logo */}
                <div className="d-flex justify-content-between border-bottom pb-3 mb-4">
                  <div className="d-flex align-items-center">
                    <img src="/assets/img/logo.png" alt="Aloha Logo" style={{ height: "40px" }} />
                  </div>
                  <div className="text-end">
                    <h2 className="mb-1 text-primary">RECEIPT</h2>
                    <p className="text-muted mb-0">Ref: {refString}</p>
                    <p className="text-muted mb-0">
                      Date: {format(new Date(), "dd MMM yyyy")}
                    </p>
                  </div>
                </div>

                {/* Info Sections */}
                <div className="row mb-4">
                  <div className="col-sm-6">
                    <h6 className="text-uppercase text-muted mb-2">Billed To:</h6>
                    <p className="mb-0 fw-bold">{studentData?.name}</p>
                    <p className="mb-0 text-muted">ID: {studentData?.profile?.studentid || 'N/A'}</p>
                    <p className="mb-0 text-muted">Course: {primaryInvoice?.courseName}</p>
                  </div>
                  <div className="col-sm-6 text-sm-end">
                    <h6 className="text-uppercase text-muted mb-2">Payment Details:</h6>
                    <p className="mb-0 text-muted">Invoice Date: {primaryInvoice?.invoiceDate}</p>
                    {!isGrouped && (
                      <p className="mb-0 text-muted">Stage: {primaryInvoice?.stageName}</p>
                    )}
                    <p className="mb-0 text-muted text-success fw-bold">Status: Paid</p>
                  </div>
                </div>

                {/* Amount Table */}
                <div className="table-responsive mb-4">
                  <table className="table table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Description</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices!.map((inv: any, idx: number) => (
                        <tr key={idx}>
                          <td>
                            {inv.type === 'registrationfee' ? 'Registration Fee' : 'Course Fee'}
                            {' - '}
                            {inv.stageName}
                          </td>
                          <td className="text-end">₹{Number(inv.amount).toLocaleString()}</td>
                        </tr>
                      ))}
                      {totalDiscount > 0 && (
                        <tr>
                          <td>Discount</td>
                          <td className="text-end text-danger">-₹{totalDiscount.toLocaleString()}</td>
                        </tr>
                      )}
                      <tr className="table-active fw-bold">
                        <td>Total Paid</td>
                        <td className="text-end">₹{totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Remarks */}
                {primaryInvoice?.offlineRemarks && (
                  <div
                    className="mb-4 p-3 rounded"
                    style={{ background: "#f8f9fa", borderLeft: "4px solid #6c757d" }}
                  >
                    <h6 className="text-uppercase text-muted mb-1" style={{ fontSize: "0.7rem", letterSpacing: "0.08em" }}>
                      Remarks
                    </h6>
                    <p className="mb-0" style={{ fontSize: "0.9rem", color: "#343a40", whiteSpace: "pre-wrap" }}>
                      {primaryInvoice.offlineRemarks}
                    </p>
                  </div>
                )}

                {/* Footer Message */}
                <div className="text-center mt-5 pt-3 border-top">
                  <p className="text-muted mb-0">Thank you for your payment!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReceiptModal;
